import { stores } from '@kurtosys/app-start';
import { ILogOptions, TLogType } from '@kurtosys/app-start/dist/appStartUtils';
import { models } from '@kurtosys/event-manager';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { common, query } from '@kurtosys/ksys-app-template';
import { TEntityType } from '@kurtosys/types/api/common/TEntityType.js';
import { IFundGetFiltersRequest } from '@kurtosys/types/api/search/IFundGetFiltersRequest.js';
import { IFundGetFiltersResponse } from '@kurtosys/types/api/search/IFundGetFiltersResponse.js';
import { ISearch } from '@kurtosys/types/api/search/ISearch.js';
import { IFilterProperty } from '@kurtosys/types/app/filters/IFilterProperty.js';
import { IFilterPropertyValue } from '@kurtosys/types/app/filters/IFilterPropertyValue.js';
import { IFilterPropertyValueSelection } from '@kurtosys/types/app/filters/IFilterPropertyValueSelection.js';
import { TFilterProperties } from '@kurtosys/types/app/filters/TFilterProperties.js';
import { TSelectionState } from '@kurtosys/types/app/filters/TSelectionState.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { IInputBaseFilterProps } from '../../../models/input/IInputBaseFilterProps.js';
import { IMegaMenuFilterProps } from '../../MegaMenu/models/IMegaMenuFilterProps.js';
import { IMenuFilterProps } from '../../Menu/models/IMenuFilterProps.js';
import { InputType } from '../models/InputType.js';

import { IFilterParentReference, PropertyMappingStore } from './clientConfigurations/PropertyMappingStore.js';
import { AppStore } from './AppStore.js';
import { ComponentInputsStore } from './ComponentsInputsStore.js';
import { GlobalInputsStore } from './GlobalInputsStore';

type TKurtosysApiStore = stores.KurtosysApiStore<IConfiguration, IComponentStyles>;

interface IFilterRequest {
	entityType: TEntityType;
	properties: string[];
	counts: boolean;
}

type TRawFilterTypes = IInputBaseFilterProps | IMegaMenuFilterProps | IMenuFilterProps;

type TFilterPropertiesWithValues = Partial<Record<TEntityType, Record<string, IFilterPropertyValue[]>>>;

type TFilterCallback = () => Promise<void>;

interface ISetFilterValueOptions {
	forceExecution?: boolean;
	isInitialLoad?: boolean;
	isSync?: boolean;
}

type SyncChangeType = 'none' | 'add' | 'update';
interface SyncChangeBase {
	type: SyncChangeType;
}
interface SyncChangeEntityBase extends SyncChangeBase {
	entityType: TEntityType;
	entityProperty: string;
	filterProperty: IFilterProperty;
}
interface SyncChangeNone extends SyncChangeBase {
	type: 'none';
}
interface SyncChangeAdd extends SyncChangeEntityBase {
	type: 'add';
}
interface SyncChangeUpdate extends SyncChangeEntityBase {
	type: 'update';
	additions: string[];
	removals: string[];
}
type SyncChange = SyncChangeAdd | SyncChangeUpdate | SyncChangeNone;

export class FilterPropertyStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	// Populated via inputs configuration
	private properties: TFilterProperties = {};

	private selectedPropertyValueKeys: string[] = [];

	private filterCallbacks: TFilterCallback[] = [];

	// Populated via Global Search App
	private searchTerm: string | null = null;

	// Abort Controller
	private abortController: AbortController;

	@observable.ref
	public filterValues: TFilterPropertiesWithValues = {};

	@observable
	selectedFilters = 0;
	// The client configuration mapping, this should not change.
	// The key of the map will be an compound value of `{entityType}|{property}`
	// Then the configured property values as the second key, if no subMapping then we will instantiate an empty map
	// else a new map with its configured compound key and a string set with the property values
	/**
	 * {
	 *   `{entityType}|{property}`: {
	 *     `{filterValue}`: {
	 *       `{entityType}|{property}`: [...values]
	 *     },
	 *     `{filterValue}`: {}
	 *   }
	 * }
	 */
	clientConfigurationFilterMapping: Map<string, Map<string, Map<string, Set<string>>>> = new Map();

	@computed
	private get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	private get eventBusStore() {
		return this.storeContext.get<stores.EventBusStore<IConfiguration, IComponentStyles>>('eventBusStore');
	}

	@computed
	private get globalInputsStore() {
		return this.storeContext.get<GlobalInputsStore>('globalInputsStore');
	}
	
	// debouncing
	debounceTimeout_Count: NodeJS.Timeout | undefined;
	debounceTimeout_Event: NodeJS.Timeout | undefined;

	@computed
	get debounceEnabled_Count(): boolean {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return !!(appStore.appComponentConfiguration?.features?.filterCountDebounce?.enabled);
	}

	@computed
	get debounceTime_Count(): number {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return (appStore.appComponentConfiguration?.features?.filterCountDebounce?.debounceTime) || 500;
	}

	@computed
	get debounceEnabled_Event(): boolean {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return !!(appStore.appComponentConfiguration?.features?.filterEventDebounce?.enabled);
	}

	@computed
	get debounceTime_Event(): number {
		const appStore = this.storeContext.get<AppStore>('appStore');
		return (appStore.appComponentConfiguration?.features?.filterEventDebounce?.debounceTime) || 500;
	}

	@action
	private debounceGetFilters = async (filterRequestConfig: IFilterRequest, search?: ISearch[], callback?: (response: IFundGetFiltersResponse) => void): Promise<void> => {
		if (this.debounceEnabled_Count) {
			if (this.debounceTimeout_Count) {
				clearTimeout(this.debounceTimeout_Count);
				this.debounceTimeout_Count = undefined;
			}
			this.debounceTimeout_Count = setTimeout(() => {
				this.getFilters(filterRequestConfig, search, callback);
			}, this.debounceTime_Count);
		}
		else {
			this.getFilters(filterRequestConfig, search, callback);
		}
	};

	@action
	private debounceNotifyEventBus = async (): Promise<void> => {
		if (this.debounceEnabled_Event) {
			if (this.debounceTimeout_Event) {
				clearTimeout(this.debounceTimeout_Event);
				this.debounceTimeout_Event = undefined;
			}
			this.debounceTimeout_Event = setTimeout(() => {
				this.notifyEventBus();
			}, this.debounceTime_Event);
		}
		else {
			this.notifyEventBus();
		}
	};

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@action
	registerCallback = (callback: TFilterCallback) => {
		this.filterCallbacks.push(callback);
	};

	@action fireCallbacks = async () => {
		const promises = this.filterCallbacks.map((callback) => {
			new Promise(callback);
		});
		await Promise.all(promises);
	};

	@action
	clearAllFilters = () => {
		let stateUpdated = false;
		for (const entityType in this.properties) {
			for (const property in this.properties[entityType]) {
				if (this.properties[entityType][property]['selectedValues']) {
					const { selectedValues } = this.properties[entityType][property];
					const selectedValuesKeys = Object.keys(selectedValues);
					selectedValuesKeys.forEach((selectedValueKey) => {
						if (
							this.toggleFilterValueOff(
								{
									entityType: entityType as TEntityType,
									property,
								},
								this.properties[entityType][property]['selectedValues'][selectedValueKey],
							)
						) {
							stateUpdated = true;
						}
					});
				}
			}
		}
		if (stateUpdated) {
			this.setFilterValues({
				forceExecution: true,
			});
		}
	};

	@action
	updateSelectedFilters = () => {
		let selectedFilters = 0;
		for (const entityType in this.properties) {
			for (const property in this.properties[entityType]) {
				if (this.properties[entityType][property]['selectedValues']) {
					const selectedItemsLength = Object.keys(
						this.properties[entityType][property]['selectedValues'],
					).length;
					if (selectedItemsLength > 0) {
						selectedFilters++;
					}
				}
			}
		}
		this.selectedFilters = selectedFilters;
	};

	getFilterPropertyValues(filter: TRawFilterTypes, mappingKey?: string): IFilterPropertyValueSelection[] {
		const { entityType, property } = filter;
		const values = this.properties[entityType][property].possibleValues || [];
		const filterValues = (this.filterValues[entityType] && this.filterValues[entityType][property]) || [];

		const filterKeys = this.propertyMappingStore.getAllowedFilterValues(
			entityType,
			property,
			Object.keys(values),
			mappingKey,
		);
		const response: IFilterPropertyValueSelection[] = filterKeys.map((key) => {
			const value = values[key];
			const matchingValue = filterValues.find((filterValue) => filterValue.value === value.value);
			const childFilterDetail = this.getChildFilterDetail(filter, value.value);
			const selectedItem = this.getSelectedItem(filter, key);
			return {
				...value,
				isSelected: selectedItem && selectedItem.isSelected,
				selectionState: selectedItem && selectedItem.selectionState,
				count: (matchingValue && matchingValue.count) || 0,
				childFilter: childFilterDetail?.childFilter,
			};
		});
		return response;
	}

	private getFilterProperty(filter: TRawFilterTypes): IFilterProperty {
		const { entityType, property } = filter;
		return this.properties[entityType] && this.properties[entityType][property];
	}

	private getSelectedItem(filter: TRawFilterTypes, key: string): IFilterPropertyValueSelection {
		const { selectedValues } = this.getFilterProperty(filter);
		return selectedValues && selectedValues[key];
	}

	public getChildFilterDetail(
		filter: TRawFilterTypes,
		filterValue: string,
	): { childFilter: TRawFilterTypes; values: IFilterPropertyValueSelection[] } | undefined {
		const { entityType, property } = filter;
		const childFilter = this.propertyMappingStore.getFilterChildReference(entityType, property, filterValue);
		if (childFilter) {
			return {
				childFilter,
				values: this.getFilterPropertyValues(childFilter, childFilter.key),
			};
		}
		return;
	}

	@computed
	get propertyMappingStore(): PropertyMappingStore {
		return this.storeContext.get<PropertyMappingStore>('propertyMappingStore');
	}

	@computed
	get hasApplyFiltersButton(): boolean {
		return this.appStore.hasApplyFiltersButton;
	}

	@action
	public async initialize(callbacks?: Record<string, EventListener>): Promise<void> {
		this.subscribeToGroupEvent();
		const componentInputsStore = this.storeContext.get<ComponentInputsStore>('componentInputsStore');
		const filterProperties: TFilterProperties = {};
		const filters: TRawFilterTypes[] = [];
		// Pull filter configs off of each input type
		for (const input of componentInputsStore.inputConfigurations) {
			switch (input.type) {
				case InputType.DatePicker:
					if (input.options.datePicker.filter) {
						filters.push(input.options.datePicker.filter);
					}
					break;
				case InputType.Dropdown:
					if (input.options.dropdown.filter) {
						filters.push(input.options.dropdown.filter);
					}
					break;
				case InputType.MegaMenu:
					if (input.options.megaMenu.filters) {
						input.options.megaMenu.filters.forEach((filter) => {
							if (filter) {
								filters.push(filter);
							}
						});
					}
					break;
				case InputType.Menu:
					if (input.options.menu.filter) {
						filters.push(input.options.menu.filter);
					}
					break;
				default:
					// DO NOTHING, new input types that use filters will need to be added.
					break;
			}
		}
		// Build filter property record
		if (filters.length > 0) {
			for (const filter of filters) {
				const { entityType, property, propertyDataType } = filter;
				const counts = this.mustFilterDisplayCount(filter);
				if (!filterProperties[entityType]) {
					filterProperties[entityType] = {};
				}
				if (!filterProperties[entityType][property]) {
					filterProperties[entityType][property] = {
						counts,
						entityType: filter.entityType,
						property: filter.property,
						propertyDataType,
					};
				} else if (counts === true && filterProperties[entityType][property].counts === false) {
					filterProperties[entityType][property].counts = true;
				}
			}
		}
		// Add nested properties
		this.propertyMappingStore.applyNestedProperties(filterProperties);
		// Fetch initial list of all possible property values
		const entityTypes = Object.keys(filterProperties) as TEntityType[];
		for (const entityType of entityTypes) {
			let counts = false;
			const properties = Object.keys(filterProperties[entityType]);
			for (const property of properties) {
				if (!counts && this.mustFilterDisplayCount(filterProperties[entityType][property])) {
					counts = true;
				}
			}
			const filterRequest: IFilterRequest = {
				entityType,
				counts,
				properties,
			};
			try {
				// Fetch all possible filter values, before search terms or filter selections
				const callback = (resp: IFundGetFiltersResponse) => {
					for (const property of properties) {
						let possibleValues = {};
						if (resp[property] && resp[property].length > 0) {
							possibleValues = resp[property].reduce((values, item) => {
								const valueKey = this.getFilterItemKey(item);
								values[valueKey] = item;
								return values;
							}, {});
						}
						filterProperties[entityType][property].possibleValues = possibleValues;
					}
				};
				await this.getFilters(filterRequest, [], callback, true);
			} catch (e) {
				const appStore = this.storeContext.get<AppStore>('appStore');
				appStore.logDebug(`Failed to get filters`, filterRequest);
			}
			this.properties = filterProperties;
			await this.setFilterValues({ forceExecution: true, isInitialLoad: true });
		}
		this.queryClientConfigurationFilterMapping();
	}

	queryClientConfigurationFilterMapping = () => {
		// Query the mapping and filter out the disabled keys
		const filterMappingConfig = this.getQueryValue({
			queryOptionsType: 'clientConfiguration',
			code: 'filter_mapping',
			key: 'values',
		});
		this.orchestrateClientConfigFilterMapping(filterMappingConfig);
	};

	orchestrateClientConfigFilterMapping = (filterMappingConfigs: any) => {
		if (filterMappingConfigs) {
			for (const filterMappingConfig of filterMappingConfigs) {
				if (!filterMappingConfig.disabled) {
					const { entityType, property, values } = filterMappingConfig;
					// This top level should have properties configured to have a successful map
					if (entityType && property && values) {
						const mapKey = `${entityType}|${property}`;
						const filterMap = this.clientConfigurationFilterMapping.get(mapKey);
						if (filterMap) {
							console.warn(
								`Client Configuration Filter Mapping: Duplicate filter property ${property} found for entity type ${entityType}. Please review filter mapping configuration`,
							);
						} else {
							const valueMap = new Map();
							// This should be the values that is configured to display, the filter api might return more which will be ignored
							const filterPropertyValues = Object.keys(values);
							// Check if the value is configured to display or if it has a sub mapping
							for (const filterPropertyValue of filterPropertyValues) {
								const filterPropertyValueConfig = values[filterPropertyValue];
								if (!filterPropertyValueConfig.disabled) {
									// If there is sub mapping config, build that before adding to the map
									const {
										entityType: subEntityType,
										property: subProperty,
										values: subValues,
									} = filterPropertyValueConfig;
									const subMap = new Map();
									if (subEntityType && subProperty && subValues) {
										const subMapKey = `${subEntityType}|${subProperty}`;
										const subFilterPropertyValues = Object.keys(subValues);
										subMap.set(subMapKey, new Set([...subFilterPropertyValues]));
									}
									valueMap.set(filterPropertyValue, subMap);
								}
							}
							this.clientConfigurationFilterMapping.set(mapKey, valueMap);
						}
					}
				}
			}
		}
	};

	@action
	public toggleFilterValueSelection(filter: TRawFilterTypes, filterItem: IFilterPropertyValue) {
		this.log('debug', {
			message: `Toggle filter value selection`,
			detail: {
				filter,
				filterItem
			}
		});
		const selectedFilterItemKey = this.getSelectedFilterItemKey(filter, filterItem);
		let stateUpdated = false;
		if (this.selectedPropertyValueKeys.includes(selectedFilterItemKey)) {
			stateUpdated = this.toggleFilterValueOff(filter, filterItem);
		} else {
			stateUpdated = this.toggleFilterValueOn(filter, filterItem, 'full');
		}
		if (stateUpdated) {
			this.setFilterValues();
		}
	}

	/**
	 * Update the selection state of a filter property
	 *
	 * @private
	 * @param filter
	 * @param value
	 * @param [selectionState]
	 * @returns a boolean based on the comparison of of the current selection value: true if the state needed to be updated, false if state did not need to be updated.
	 */
	private toggleFilterValueOn(
		filter: TRawFilterTypes,
		value: IFilterPropertyValue | string,
		selectionState?: TSelectionState,
	): boolean {
		const filterProperty = this.getFilterProperty(filter);
		if (filterProperty) {
			const filterItem: IFilterPropertyValue =
				typeof value === 'string' ? filterProperty.possibleValues[value] : value;

			// don't apply updates if it is already selected
			const filterItemKey = this.getFilterItemKey(filterItem);
			if (filterProperty.selectedValues?.[filterItemKey]?.isSelected) {
				return false;
			}

			// If the state has not changed, do not continue
			const previousSelection = filterProperty.selectedValues && filterProperty.selectedValues[filterItemKey];
			if (previousSelection && previousSelection.selectionState === selectionState) {
				return false;
			}

			// push onto quick ref (not observable)
			const selectedFilterItemKey = this.getSelectedFilterItemKey(filter, filterItem);
			if (!this.selectedPropertyValueKeys.includes(selectedFilterItemKey)) {
				this.selectedPropertyValueKeys.push(selectedFilterItemKey);
			}

			// update tree (observable)
			if (!filterProperty.selectedValues) {
				filterProperty.selectedValues = {};
			}
			filterProperty.selectedValues[filterItemKey] = {
				...filterItem,
				isSelected: true,
				selectionState,
			};

			let itemsToRecalc: any[] = [];
			let filterValue;

			if (filterItem && filterItem.value) {
				filterValue = Array.isArray(filterItem.value) ? filterItem.value : [filterItem.value];
			}

			if (filterValue && filterValue.length > 0 && selectionState === 'full') {
				// Check for Children
				const childFilterReference = this.getChildFilterDetail(filter, filterValue[0]);
				if (childFilterReference && childFilterReference.values.length > 0) {
					const { childFilter, values } = childFilterReference;
					const childFilterProperty = this.getFilterProperty(childFilter);
					for (const childItem of values) {
						const childFilterItemKey = this.getFilterItemKey(childItem);
						if (!filterProperty.selectedValues[childFilterItemKey]) {
							// push onto quick ref (not observable)
							const selectedFilterItemKey = this.getSelectedFilterItemKey(childFilter, childItem);
							if (!this.selectedPropertyValueKeys.includes(selectedFilterItemKey)) {
								this.selectedPropertyValueKeys.push(selectedFilterItemKey);
							}
							// update tree (observable)
							if (!childFilterProperty.selectedValues) {
								childFilterProperty.selectedValues = {};
							}
							childFilterProperty.selectedValues[childFilterItemKey] = {
								...childItem,
								isSelected: true,
								selectionState: 'full',
							};
							// Look for additional parents
							const childFilterValue = Array.isArray(childItem.value)
								? childItem.value
								: [childItem.value];
							const parents =
								this.propertyMappingStore.getParentReferences(
									childFilter.entityType,
									childFilter.property,
									childFilterValue,
								) || [];
							itemsToRecalc.push(...parents);
						}
					}
				} else {
					// Check for Parents
					itemsToRecalc =
						this.propertyMappingStore.getParentReferences(
							filter.entityType,
							filter.property,
							filterValue,
						) || [];
				}
			}

			// Recalculate parent values
			for (const itemToRecalc of itemsToRecalc) {
				const { value, ...parentFilter } = itemToRecalc;
				const selectionState = this.getParentSelectionState(itemToRecalc);
				if (selectionState === 'full' || selectionState === 'half') {
					this.toggleFilterValueOn(parentFilter, value, selectionState);
				} else {
					this.toggleFilterValueOff(parentFilter, value);
				}
			}
			this.properties = { ...this.properties };
			return true;
		}
		return false;
	}

	/**
	 * Update the selection state of a filter property
	 *
	 * @private
	 * @param filter
	 * @param value
	 * @returns a boolean based on the comparison of of the current selection value: true if the state needed to be updated, false if state did not need to be updated.
	 */
	private toggleFilterValueOff(filter: TRawFilterTypes, value: IFilterPropertyValue | string) {
		const filterProperty = this.getFilterProperty(filter);
		if (filterProperty) {
			const filterItem: IFilterPropertyValue =
				typeof value === 'string' ? filterProperty.possibleValues[value] : value;

			// don't apply updates if it doesn't exist
			const filterItemKey = this.getFilterItemKey(filterItem);
			if (!filterProperty.selectedValues?.[filterItemKey]) {
				return false;
			}

			// If the state has not changed, do not continue
			const previousSelection = filterProperty.selectedValues && filterProperty.selectedValues[filterItemKey];
			if (!previousSelection || previousSelection.selectionState === 'none') {
				return false;
			}

			const selectedFilterItemKey = this.getSelectedFilterItemKey(filter, filterItem);

			if (filterProperty.selectedValues[filterItemKey]) {
				// remove from quick ref (not observable)
				const selectedFilterItemIndex = this.selectedPropertyValueKeys.indexOf(selectedFilterItemKey);
				if (selectedFilterItemIndex > -1) {
					this.selectedPropertyValueKeys.splice(selectedFilterItemIndex, 1);
				}
				// remove from tree (observable)
				delete filterProperty.selectedValues[filterItemKey];
			}

			let itemsToRecalc: any[] = [];
			let filterValue;

			if (filterItem && filterItem.value) {
				filterValue = Array.isArray(filterItem.value) ? filterItem.value : [filterItem.value];
			}

			if (filterValue && filterValue.length > 0) {
				// Check for Children
				const childFilterReference = this.getChildFilterDetail(filter, filterValue[0]);
				if (childFilterReference && childFilterReference.values.length > 0) {
					const { childFilter, values } = childFilterReference;
					const childFilterProperty = this.getFilterProperty(childFilter);
					if (childFilterProperty.selectedValues) {
						for (const childItem of values) {
							const childFilterItemKey = this.getFilterItemKey(childItem);
							if (childFilterProperty.selectedValues[childFilterItemKey]) {
								// remove from quick ref (not observable)
								const selectedChildFilterItemKey = this.getSelectedFilterItemKey(
									childFilter,
									childItem,
								);
								const childSelectedFilterItemIndex =
									this.selectedPropertyValueKeys.indexOf(selectedChildFilterItemKey);
								if (childSelectedFilterItemIndex > -1) {
									this.selectedPropertyValueKeys.splice(childSelectedFilterItemIndex, 1);
								}
								// remove from tree (observable)
								delete childFilterProperty.selectedValues[childFilterItemKey];
								// Look for additional parents
								const childFilterValue = Array.isArray(childItem.value)
									? childItem.value
									: [childItem.value];
								const parents =
									this.propertyMappingStore.getParentReferences(
										childFilter.entityType,
										childFilter.property,
										childFilterValue,
									) || [];
								itemsToRecalc.push(...parents);
							}
						}
					}
				} else {
					// Check for Parents
					itemsToRecalc =
						this.propertyMappingStore.getParentReferences(
							filter.entityType,
							filter.property,
							filterValue,
						) || [];
				}
			}

			// Recalculate parent values
			for (const itemToRecalc of itemsToRecalc) {
				const { value, ...parentFilter } = itemToRecalc;
				const selectionState = this.getParentSelectionState(itemToRecalc);
				if (selectionState === 'half') {
					this.toggleFilterValueOn(parentFilter, value, 'half');
				} else {
					this.toggleFilterValueOff(parentFilter, value);
				}
			}
			this.properties = { ...this.properties };
			return true;
		}
		return false;
	}

	private getParentSelectionState(filterParent: IFilterParentReference): TSelectionState {
		const { value, ...parentFilter } = filterParent;
		const { values: childValues = [] } = this.getChildFilterDetail(parentFilter, value);
		const selectedChildren = childValues.filter((childValue) => childValue.isSelected);
		const total = childValues.length;
		const totalSelected = selectedChildren.length;
		return total === totalSelected ? 'full' : totalSelected > 0 ? 'half' : 'none';
	}

	@action
	public updateFilterValueSelections(
		filter: TRawFilterTypes,
		value: IFilterPropertyValue | IFilterPropertyValue[],
	): void {
		this.log('debug', {
			message: `Update filter value selections`,
			detail: {
				filter,
				value
			}
		});
		let stateUpdated = false
		const filterProperty = this.getFilterProperty(filter);
		if (filterProperty) {
			if (filterProperty.selectedValues) {
				const oldFilterItems = Object.keys(filterProperty.selectedValues);
				oldFilterItems.forEach((oldFilterItem) => {
					if (this.toggleFilterValueOff(filter, oldFilterItem)) {
						stateUpdated = true
					}
				});
			}
			const filterItems = Array.isArray(value) ? value : [value];
			for (const filterItem of filterItems) {
				if (this.toggleFilterValueOn(filter, filterItem, 'full')) {
					stateUpdated = true;
				}
			}
		}
		if (stateUpdated) {
			this.setFilterValues();
		}
	}

	@action
	public clearFilterValueSelection(filter: TRawFilterTypes): void {
		this.log('debug', {
			message: `Clearing filer value selection`,
			detail: {
				filter
			}
		});
		const filterProperty = this.getFilterProperty(filter);
		if (filterProperty && filterProperty.selectedValues) {
			let stateUpdated = false;
			Object.keys(filterProperty.selectedValues).forEach((selectedKey) => {
				if (this.toggleFilterValueOff(filter, filterProperty.selectedValues[selectedKey])) {
					stateUpdated = true;
				}
			});
			if (stateUpdated) {
				this.setFilterValues();
			}
		}
	}

	@action
	public clearChildFilterValueSelections(filter: TRawFilterTypes, filterValue: string): void {
		this.log('debug', {
			message: `Clearing child filter value selections`,
			detail: {
				filter,
				filterValue
			}
		});
		const childFilterDetail = this.getChildFilterDetail(filter, filterValue);
		if (childFilterDetail && childFilterDetail.values) {
			let stateUpdated = false;
			for (const childItem of childFilterDetail.values) {
				if (this.toggleFilterValueOff(childFilterDetail.childFilter, childItem)) {
					stateUpdated = true;
				}
			}
			if (stateUpdated) {
				this.setFilterValues();
			}
		}
	}

	@action
	public setSearchTerm = (searchTerm: string) => {
		this.log('debug', {
			message: `Update filter counts based on search`,
			detail: {
				searchTerm
			}
		});
		// Setting an empty string to undefined to not include the search filter parameters in the get filter call
		const newSearchTerm = searchTerm === '' ? undefined : searchTerm;
		if (newSearchTerm !== this.searchTerm) {
			this.searchTerm = newSearchTerm;
			this.setFilterValues();
		}
	};

	@action
	public async setFilterValues({ forceExecution = false, isInitialLoad = false, isSync = false }: ISetFilterValueOptions = {}): Promise<void> {
		this.log('debug', {
			message: `Set filter values`,
			detail: {
				forceExecution,
				isInitialLoad,
				isSync,
				hasApplyFiltersButton: this.hasApplyFiltersButton
			}
		});
		if (!this.hasApplyFiltersButton || forceExecution) {
			if (!isInitialLoad && !isSync) {
				// Let other apps start updating e.g. table firing searchEntity, while we fire the getFilters call to update our counts.
				this.debounceNotifyEventBus();
			}
			// Fire getFilters API calls per entity type and return based on search term and/or filter selections
			const entityTypes = Object.keys(this.properties) as TEntityType[];
			const filterValues: TFilterPropertiesWithValues = {};
			for (const entityType of entityTypes) {
				const searchBody = this.getSearchBody(entityType);
				let counts = false;
				const properties = Object.keys(this.properties[entityType]);
				for (const property of properties) {
					if (!counts && this.mustFilterDisplayCount(this.properties[entityType][property])) {
						counts = true;
					}
				}
				const filterRequest: IFilterRequest = {
					entityType,
					counts,
					properties,
				};
				try {
					const callback = (resp) => {
						for (const property of properties) {
							if (resp[property] && resp[property].length > 0) {
								if (!filterValues[entityType]) {
									filterValues[entityType] = {};
								}
								if (
									!filterValues[entityType][property] &&
									this.properties[entityType][property].possibleValues
								) {
									if (counts) {
										const possibleValues = Object.keys(
											this.properties[entityType][property].possibleValues,
										);
										const mappedPossibleValues = this.applyPropertyMapping(
											entityType,
											property,
											possibleValues,
										);
										filterValues[entityType][property] = this.orchestrateFilterResponse(
											resp,
											entityType,
											property,
											mappedPossibleValues,
											true,
										);
									} else {
										const respValues = resp[property].map((item) => {
											return this.getFilterItemKey(item);
										});
										const mappedValues = this.applyPropertyMapping(entityType, property, respValues);
										filterValues[entityType][property] = this.orchestrateFilterResponse(
											resp,
											entityType,
											property,
											mappedValues,
										);
									}
								}
							}
						}
						runInAction(() => {
							this.filterValues = filterValues;
							this.updateSelectedFilters();
						});
						this.fireCallbacks();
					};
					this.debounceGetFilters(filterRequest, searchBody, callback);
				} catch (e) {
					console.error('failed to getFilters', filterRequest, e);
				}
			}
		}
		this.fireCallbacks();
	}

	public notifyEventBus() {
		this.log('debug', {
			message: `Notify Event Bus`,
		});
		this.publishGroupEvent();
		this.globalInputsStore.setFilterValues(this.getGlobalInputsFilterValues());
		this.eventBusStore.trigger<models.detail.EventApplyFiltersDetail>({ type: EventTypes.APPLY_FILTERS });
	}

	@action
	private syncSelectedSharedFilters = (event: models.EventPublish<models.detail.EventUpdateSharedFilterDetail>) => {
		this.log('debug', {
			message: 'Syncing shared filters from group',
			detail: event
		})
		const { detail } = event;
		if (detail.properties) {
			const changes: (SyncChangeAdd | SyncChangeUpdate)[] = []
			const entityTypes = Object.keys(detail.properties);
			entityTypes.forEach((entityType: TEntityType) => {
				if (!this.properties[entityType]) {
					this.properties[entityType] = {};
				}
				const entityProperties = Object.keys(detail.properties[entityType]);
				entityProperties.forEach((entityProperty) => {
					const detailProperty = detail.properties[entityType][entityProperty];
					let change: SyncChange = {
						type: 'none'
					};
					if (!this.properties[entityType][entityProperty]) {
						this.properties[entityType][entityProperty] = {
							...detailProperty,
						} as IFilterProperty;
						change = {
							type: 'add',
							entityType,
							entityProperty,
							filterProperty: detailProperty
						};
					}
					if (change.type === 'none') {
						const currentKeys = Object.keys(this.properties[entityType][entityProperty].selectedValues || {});
						const newKeys = Object.keys(detail.properties[entityType][entityProperty].selectedValues || {});
						const selectionChange = this.getPropertySelectionSyncChanges({ currentKeys, newKeys });
						if (selectionChange.updated) {
							change = {
								type: 'update',
								entityType,
								entityProperty,
								filterProperty: detailProperty,
								additions: selectionChange.additions,
								removals: selectionChange.removals,
							}
							this.properties[entityType][entityProperty].selectedValues = detailProperty.selectedValues;
						}
					}

					if (change.type !== 'none') {
						changes.push(change);
					}
				})
			});

			changes.forEach((change) => {
				this.log('debug', { message: 'syncing selection changes', detail: { change } });
				const filterKey = this.getFilterKey(change.filterProperty);
				const addSelectedPropertyValueKey = (selectionKey) => {
					try {
						const item = change.filterProperty.selectedValues[selectionKey];
						this.selectedPropertyValueKeys.push(`${filterKey}-${this.getFilterItemKey(item)}`);
					} catch (e) {
						this.log('error', { message: 'failed to add selected property key', detail: { change } });
					}
				}
				const removeSelectedPropertyValueKey = (selectionKey) => {
					try {
						const item = change.filterProperty.selectedValues[selectionKey];
						const removeIndex = this.selectedPropertyValueKeys.findIndex((key) => `${filterKey}-${this.getFilterItemKey(item)}`)
						this.selectedPropertyValueKeys.splice(removeIndex, 1);
					} catch (e) {
						this.log('error', { message: 'failed to remove selected property key', detail: { change } });
					}
				}
				try {
					if (change.type === 'add' && change.filterProperty.selectedValues) {
						Object.keys(change.filterProperty.selectedValues).forEach(addSelectedPropertyValueKey)
					} else if (change.type === 'update') {
						change.removals.forEach(removeSelectedPropertyValueKey);
						change.additions.forEach(addSelectedPropertyValueKey)
					}
				} catch (e) {
					this.log('error', { message: 'failed to apply selection changes', detail: { change } });
				}
			});

			this.setFilterValues({ forceExecution: true, isSync: true });
		}
	}

	private getPropertySelectionSyncChanges = ({ newKeys, currentKeys }: { newKeys: string[]; currentKeys: string[] }) => {
		const response: { updated: boolean; additions: string[]; removals: string[] } = {
			updated: false,
			additions: [],
			removals: [],
		}
		for (let i = 0; i < newKeys.length; i++) {
			const key = newKeys[i];
			if (!currentKeys.includes(key)) {
				response.updated = true;
				response.additions.push(key);
			}
		}
		for (let i = 0; i < currentKeys.length; i++) {
			const key = currentKeys[i];
			if (!newKeys.includes(key)) {
				response.updated = true;
				response.removals.push(key);
			}
		}
		return response;
	}

	private subscribeToGroupEvent() {
		this.log('debug', {
			message: `Subscribe shared filters to group`,
		});
		this.appStore.groupEventSubscribe(
			models.EventTypes.UPDATE_SHARED_FILTERS,
			this.syncSelectedSharedFilters,
		);
	}

	private publishGroupEvent() {
		const eventDetail: models.EventTypeDetail = {
			type: models.EventTypes.UPDATE_SHARED_FILTERS,
			properties: this.properties,
		};
		this.log('debug', {
			message: `Publish shared filters to group`,
			detail: eventDetail
		});
		this.appStore.groupEventTrigger(eventDetail);
	}

	private getGlobalInputsFilterValues() {
		const filterValues: Record<string, any> = {};
		const entityTypes = Object.keys(this.properties) as TEntityType[];
		entityTypes.forEach((entityType) => {
			const properties = Object.keys(this.properties[entityType]);
			const entityTypeSelections: Record<string, any> = {};
			properties.forEach((property) => {
				const { selectedValues = {} } = this.properties[entityType][property];
				if (Object.keys(selectedValues).length > 0) {
					entityTypeSelections[property] = Object.keys(selectedValues).map((key) => {
						return {
							key,
							label: selectedValues[key].label,
							value: selectedValues[key].value,
						};
					});
				}
			});
			filterValues[entityType] = entityTypeSelections;
		});
		return filterValues;
	}

	private applyPropertyMapping(entityType: TEntityType, property: string, values: string[]) {
		return this.propertyMappingStore.enabled
			? this.propertyMappingStore.getAllowedFilterValues(entityType, property, values)
			: values;
	}

	private orchestrateFilterResponse(
		resp: IFundGetFiltersResponse,
		entityType: TEntityType,
		property: string,
		valueKeys: string[],
		applyCount = false,
	) {
		return valueKeys
			.reduce((values: IFilterPropertyValue[], valueKey: string) => {
				if (
					this.properties[entityType] &&
					this.properties[entityType][property] &&
					this.properties[entityType][property].possibleValues &&
					this.properties[entityType][property].possibleValues[valueKey]
				) {
					values.push(...this.valueAsArray(this.properties[entityType][property].possibleValues[valueKey]));
				}
				return values;
			}, [])
			.map((possibleValue) => {
				const { label, value } = possibleValue;
				const filteredValue = resp[property].find((v) => v.label === label && v.value === value);
				const response: IFilterPropertyValue = {
					label,
					value,
				};
				if (applyCount) {
					response.count = filteredValue?.count || 0;
				}
				return response;
			});
	}

	/**
	 * Using the GetFilters[KsysRequest], we will fetch get filtered filter values.
	 *
	 * NOTE: all request/response bodies will automatically be cached
	 *
	 * @private
	 * @param filterRequestConfig
	 * @param [search]
	 * @returns
	 */
	private async getFilters(
		filterRequestConfig: IFilterRequest,
		search?: ISearch[],
		callback?: (response: IFundGetFiltersResponse) => void,
		mustComplete?: boolean
	): Promise<void> {
		const kurtosysApiStore = this.storeContext.get<TKurtosysApiStore>('kurtosysApiStore');
		const queryStore = this.storeContext.get<stores.QueryStore<IConfiguration, IComponentStyles>>('queryStore');
		const { entityType, properties, counts } = filterRequestConfig;
		const getFilters = this.appStore.appComponentConfiguration?.features?.getFilters;

		if (this.abortController && !mustComplete) {
			this.appStore.logDebug(`Aborting getFilters request`);
			this.abortController.abort();
		}

		let body: IFundGetFiltersRequest = {
			counts,
			search: search || [],
			type: entityType,
			filterProperties: [...properties.sort((a, b) => a.localeCompare(b))],
		};

		if (getFilters?.enabled) {
			let workingRequest = getFilters?.request || {};

			if (getFilters.requestQueries) {
				const queryResults = query.queryUtils.queryHelper.convertQueryMime(
					{ queryClass: query.Query },
					getFilters.requestQueries,
					queryStore.executionOptions,
				);

				workingRequest = common.commonUtils.deepMergeObjects(workingRequest, queryResults);
			}
			body = {
				...body,
				...workingRequest,
			};
		}

		try {
			this.abortController = new AbortController();
			const signal = this.abortController.signal;
			const response = await kurtosysApiStore.getFilters.execute({ body, signal });
			if (callback) {
				callback(response);
			}
			this.abortController = undefined;
		}
		catch (ex) {
			this.appStore.logDebug(`Aborting getFilters request`, ex);
		}
	}

	private getSearchBody(entityType: TEntityType): ISearch[] {
		const searchBodies: ISearch[] = [];
		const searchTerm = this.getSearchTermBody();
		if (searchTerm) {
			searchBodies.push(searchTerm);
		}
		// If the current GI app has a groupId, filter out selectedPropertyValueKeys that have been unselected in the other app linked with the same groupId
		if (this.appStore.appComponentConfiguration.groupId) {
			this.selectedPropertyValueKeys = this.selectedPropertyValueKeys.filter((selectedPropertyValueKey) => {
				const [entityType, property, selectedItemKey] = selectedPropertyValueKey.split('-');
				return this.properties[entityType][property].selectedValues[selectedItemKey];
			});
		}
		for (let index = 0; index < this.selectedPropertyValueKeys.length; index++) {
			const key = this.selectedPropertyValueKeys[index];
			const selectedMeta = this.getSelectedFilterItemMetaFromKey(key);
			if (selectedMeta.entityType === entityType) {
				const selectedItemKey = selectedMeta.itemLabel;
				const selectedValue =
					this.properties[selectedMeta.entityType][selectedMeta.property].selectedValues[selectedItemKey];
				// Find existing search body for filter selection ()
				const existingSearchBodyIndex = searchBodies.findIndex(
					(body) => body.property === selectedMeta.property && body.matchtype === 'MATCH',
				);
				const propertyDataType =
					this.properties[selectedMeta.entityType][selectedMeta.property].propertyDataType;
				let typedValue: any = selectedValue.value;
				//Make sure the properties are passed as the correct type
				if (propertyDataType === 'number' && common.commonUtils.canBeNumber(selectedValue.value)) {
					typedValue = new Number(selectedValue.value).valueOf();
				} else if (propertyDataType === 'boolean') {
					typedValue = new Boolean(selectedValue.value).valueOf();
				} else if (['number', 'boolean'].indexOf(typeof selectedValue.value) >= 0) {
					// default data type as string
					typedValue = selectedValue.value.toString();
				}
				if (existingSearchBodyIndex >= 0) {
					searchBodies[existingSearchBodyIndex].values.push(...this.valueAsArray(typedValue));
				} else {
					searchBodies.push({
						property: selectedMeta.property,
						matchtype: 'MATCH',
						values: this.valueAsArray(typedValue),
					});
				}
			}
		}
		return searchBodies.sort((a, b) => a.property.localeCompare(b.property));
	}

	private getSearchTermBody(): ISearch | undefined {
		// Include the Global Search Term
		// Since the global search options can specify the match type and the current solution
		// hardcodes it, just add it. Will need review if the filters match type can be configured
		// and requirements are set if the match type does not match but uses the same property/values
		if (this.searchTerm && this.appStore.globalSearchOptions) {
			return {
				...this.appStore.globalSearchOptions,
				values: [this.searchTerm],
			};
		}
		return;
	}

	private getFilterKey(filter: TRawFilterTypes | IFilterProperty): string {
		return `${filter.entityType}-${filter.property}`;
	}

	private getFilterItemKey(item: { value?: string | string[] }) {
		if (item && item.value) {
			return common.commonUtils.isArrayLike(item.value) ? item.value.join('|') : item.value;
		}
	}

	private getSelectedFilterItemKey(filter: TRawFilterTypes | IFilterProperty, item: { value?: string | string[] }) {
		return `${this.getFilterKey(filter)}-${this.getFilterItemKey(item)}`;
	}

	/**
	 * Ensure that keys containing a dash (e.g. date values) are handled correctly
	 */
	private getSelectedFilterItemMetaFromKey(key: string) {
		const [entityType, property, ...itemLabel] = key.split('-');
		return {
			entityType: entityType as TEntityType,
			property,
			itemLabel: itemLabel.join('-'),
		};
	}

	private mustFilterDisplayCount(filter: TRawFilterTypes | IFilterProperty): boolean {
		if ('displayCount' in filter) {
			return filter.displayCount;
		}
		if ('counts' in filter) {
			return filter.counts;
		}
		return false;
	}

	private valueAsArray<TValue>(value: TValue | TValue[]): TValue[] {
		if (common.commonUtils.isArrayLike(value)) {
			return value;
		}
		return [value];
	}

	private log(type: TLogType, options: ILogOptions) {
		this.appStore.log(type, {
			...options,
			additionalContext: 'FilterPropertyStore',
		});
	}
}
